/**
 * env.config.ts
 * Type-safe environment variable validation using envalid.
 *
 * This replaces the custom verify-env-docs.js script with a runtime-validated,
 * self-documenting schema that provides TypeScript inference and IDE support.
 *
 * @see https://github.com/af/envalid
 */

import { cleanEnv, str, bool } from 'envalid';

// In browser context, use import.meta.env; in Node (validation script), use process.env
const rawEnv =
  typeof import.meta !== 'undefined' && import.meta.env
    ? {
        ...import.meta.env,
        // Envalid expects NODE_ENV for devDefault logic; map Vite's MODE to NODE_ENV
        NODE_ENV: import.meta.env.MODE,
      }
    : process.env;

/**
 * Validated environment configuration for Atlas Viewer.
 *
 * All variables are validated at module load time. Missing required variables
 * or invalid formats will throw descriptive errors.
 *
 * Usage:
 *   import { env } from '@/env.config';
 *   const token = env.VITE_MAPBOX_TOKEN; // Type-safe!
 */
export const env = cleanEnv(rawEnv, {
  // ============================================================================
  // App Meta
  // ============================================================================
  VITE_APP_TITLE: str({
    default: 'Atlas Viewer',
    desc: 'Application window title',
  }),
  VITE_APP_DESCRIPTION: str({
    default: 'A web-based geospatial data visualization tool',
    desc: 'Meta description for SEO',
  }),
  VITE_APP_VERSION: str({
    devDefault: '0.0.0-dev',
    desc: 'Build version (set by CI in production)',
  }),
  VITE_APP_FEEDBACK_URL: str({
    default: '',
    desc: 'Feedback form URL',
  }),
  VITE_PUBLIC_URL: str({
    default: '/',
    desc: 'Public asset base URL',
  }),
  VITE_DOCS_URL: str({
    default: '',
    desc: 'Documentation site URL (e.g., /docs/ or external URL)',
  }),
  VITE_ISSUE_SUBMISSION_ENABLED: bool({
    default: true,
    desc: 'Build-time flag to enable in-app bug report submission. To disable in production when the Azure Functions API is unavailable, set this to false in the deployment/build environment and rebuild + redeploy.',
  }),

  // ============================================================================
  // Mapbox (Optional)
  // ============================================================================
  VITE_MAPBOX_TOKEN: str({
    devDefault: '',
    desc: 'Mapbox API token (treat as secret in production)',
  }),
  VITE_MAPBOX_STYLE_URL: str({
    devDefault: '',
    desc: 'Custom Mapbox style URL',
  }),

  // ============================================================================
  // Azure Provider
  // ============================================================================
  VITE_AZURE_ACCOUNT_NAME: str({
    devDefault: '',
    desc: 'Default Azure Storage account name',
  }),
  VITE_AZURE_CONTAINER_NAME: str({
    devDefault: '',
    desc: 'Default Azure Storage container name',
  }),
  VITE_AZURE_ROOT_PREFIX: str({
    default: '',
    desc: 'Default root prefix for blob paths',
  }),
  VITE_AZURE_STRICT_CONTAINER: bool({
    default: false,
    desc: 'Enable strict container validation (fail-closed)',
  }),
  VITE_AZURE_STRICT_INTEGRITY: bool({
    default: false,
    desc: 'Force strict integrity hashing for all files',
  }),
  VITE_AZURE_DEBUG: bool({
    default: false,
    desc: 'Enable debug logging (never enable in production)',
  }),

  // ============================================================================
  // LoadSEER API Integration
  // ============================================================================
  VITE_LOADSEER_API_BASE_URL: str({
    default: '',
    desc: 'LoadSEER API base URL (e.g., /loadseer-proxy/api or https://ia-dev.loadseer.com/api)',
  }),
  VITE_LOADSEER_API_TOKEN: str({
    default: '',
    desc: 'LoadSEER API authentication token (treat as secret)',
  }),
  VITE_LOADSEER_API_ENABLED: bool({
    default: false,
    desc: 'Enable LoadSEER API integration',
  }),

  // ============================================================================
  // Testing
  // ============================================================================
  VITE_E2E_MOCK: str({
    choices: ['0', '1'],
    default: '0',
    desc: 'Enable E2E mocking (must be 0 in production)',
  }),
  VITE_TEST_SIMULATE_MISSING_SECRET: str({
    default: '',
    desc: 'Test-only: simulate missing secret scenario',
  }),
  VITE_LOCAL_SAS_SECRET: str({
    default: '',
    desc: 'Test-only: local SAS encryption passphrase',
  }),
  VITE_AZURE_TEST_FORCE_FALLBACK: bool({
    default: false,
    desc: 'Test-only: force deterministic fallback behavior',
  }),
  VITE_LOCAL_SAS_PBKDF2_ITERATIONS: str({
    default: '10000',
    desc: 'Test-only: PBKDF2 iteration count for local encryption',
  }),
});

/**
 * Type definition for the validated environment.
 * Use this for type annotations if needed.
 */
export type Env = typeof env;

/**
 * Helper to check if running in production mode.
 */
export const isProduction = env.isProd;

/**
 * Helper to check if running in development mode.
 */
export const isDevelopment = env.isDev;

/**
 * Helper to check if running in test mode.
 */
export const isTest = env.isTest;
